import { useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { api } from 'utils/axios';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import Select from './Select';

export default function AutoComplete({ options, handleAdd, filter, filter_ids, className }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [isEmail, setIsEmail] = useState(false);
  const [selectedRole, setSelectedRole] = useState(options[0]);
  const [focus, setFocus] = useState(false);

  const checkEmail = (email) => {
    const regex = /^\S+@\S+\.\S+$/;
    return regex.test(email);
  };

  const handleOnChange = (event) => {
    const lowerCase = event.target.value.toLowerCase();
    const emailValid = checkEmail(lowerCase);
    setIsEmail(emailValid);
    setSearchValue(lowerCase);

    // Auto-send when email is valid and user presses Enter or Tab
    // We'll handle email confirmation differently to avoid auto-trigger
  };

  const handleOnClick = (value) => {
    if (value.type === 'fan') {
      setSelectedRole(options[2]);
    }

    // Close the search results immediately
    setSearchResult([]);
    setSearchValue('');

    // Set the selected user properly
    const userToSelect = isEmail ? { full_name: value, id: value } : value;
    setSelectedUser(userToSelect);

    // Auto-send when user is selected
    setTimeout(() => {
      if (isEmail) {
        handleAdd(value, selectedRole, value, isEmail, true, 'email');
      } else {
        handleAdd(value?.full_name, selectedRole, value?.id, isEmail, true, value.type);
      }
      setSelectedUser('');
    }, 100);
  };

  const handleSend = () => {
    if (isEmail) {
      handleAdd(searchValue, selectedRole, searchValue, isEmail, true, 'email');
    } else {
      handleAdd(selectedUser?.full_name, selectedRole, selectedUser?.id, isEmail, true, selectedUser.type);
    }
    setSearchValue('');
    setSelectedUser('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isEmail && searchValue.trim()) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setSearchValue('');
    setSearchResult([]);
    setSelectedUser('');
  };

  const getAccounts = async () => {
    try {
      const response = await api.get('/user', {
        params: {
          search: searchValue,
        },
      });

      let result;

      if (filter) {
        result = response.data.filter((user) => user.type !== filter);
      } else {
        result = response.data;
      }
      if (filter_ids) {
        result = result.filter((participant) => !filter_ids.some((userId) => userId === participant.id));
      }
      setSearchResult(result);
    } catch (error) {
      console.log(error);
    }
  };

  useMemo(() => {
    if (searchValue.length > 1) {
      getAccounts();
    } else {
      setSearchResult([]);
    }
  }, [searchValue]);

  //  TODO  REMOVE HIDDEN USERNAMES WHEN BATTLE IS FINISHED
  const userNamesToHide = ['Batalladepromesas', 'CYPHERHOOD'];

  return (
    <>
      <div className={`flex flex-row gap-4 mb-6 ${className}`}>
        <div className='relative w-full'>
          <div className='relative'>
            <div className='relative'>
              <input
                value={searchValue}
                type='search'
                ref={inputRef}
                onChange={handleOnChange}
                onKeyDown={handleKeyDown}
                className='custom-input !pr-16 md:!pr-40'
                onFocus={() => {
                  setFocus(true);
                }}
                onBlur={() => {
                  setFocus(false);
                }}
                placeholder={t('global.placeholder.write_user_email')}
              />
              {/* <select
                className='custom-select absolute top-0 right-0 !w-auto !pr-12 !text-right !border-0 !bg-transparent'
                onChange={(e) => { setSelectedRole(e.target.value) }}
                value={selectedRole}
                disabled={selectedUser.type === 'fan'}>
                {options.map((option) =>
                  <option value={option} key={option}>{firstLetterUpperCase(option)}</option>)
                }
              </select> */}
              <div className='absolute right-0 top-0'>
                <Select
                  child
                  disabled={selectedUser.type === 'fan'}
                  options={options}
                  value={selectedRole}
                  onChange={(e) => {
                    setSelectedRole(e.target.value);
                  }}
                />
              </div>
            </div>
            <AnimatePresence>
              {searchValue && focus && (
                <motion.button
                  type='button'
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className='absolute top-0 bottom-0 right-32'
                  onClick={handleClose}
                >
                  <XMarkIcon className='h-5 w-5 text-error-red' />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <AnimatePresence>
            {/* TODO  REMOVE HIDDEN USERNAMES WHEN BATTLE IS FINISHED */}
            {searchResult.filter((user) => !userNamesToHide.includes(user.username)).length > 0 && (
              <motion.div
                className='autocomplete-container'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* TODO  REMOVE HIDDEN USERNAMES WHEN BATTLE IS FINISHED */}
                {searchResult
                  .filter((user) => !userNamesToHide.includes(user.username))
                  .map((item) => (
                    <div
                      className='autocomplete-item'
                      key={item.id}
                      onClick={() => {
                        handleOnClick(item);
                      }}
                    >
                      <span>{item.full_name}</span>
                      <span className='text-sm text-neutral-silver-300'>{item.username}</span>
                    </div>
                  ))}
                <div
                  className='autocomplete-item'
                  onClick={() => {
                    inputRef.current.focus();
                  }}
                >
                  <span className='text-neutral-silver-200 text-sm'>{t('global.searchbar')}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {/* Check button removed - auto-confirm on selection */}
      </div>
    </>
  );
}
